
import { useState, useEffect, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../integrations/supabase/client';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  phone?: string;
  avatar_url?: string;
  user_type: 'renter' | 'landlord';
  roles: ('admin' | 'landlord' | 'renter')[]; // Secure roles from user_roles table
  subscription_tier: 'renter' | 'student' | 'landlord' | 'all_in' | 'enterprise' | null;
  subscription_status: 'active' | 'trial' | 'cancelled' | 'past_due' | null;
  payment_status: 'none' | 'pending' | 'completed' | 'failed';
  payment_required: boolean;
  flock_coins: number;
  referral_code: string;
  referred_by?: string;
}

export const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const initializingRef = useRef(false);
  const profileFetchRef = useRef<string | null>(null);

  // Fetch profile function with deduplication
  const fetchProfile = async (userId: string) => {
    // Prevent duplicate fetches
    if (profileFetchRef.current === userId) {
      return;
    }
    
    profileFetchRef.current = userId;
    
    try {
      // Fetch profile data
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          user_id,
          email,
          full_name,
          phone,
          avatar_url,
          user_type,
          subscription_tier,
          subscription_status,
          payment_status,
          payment_required,
          flock_coins,
          referral_code,
          referred_by,
          created_at,
          updated_at
        `)
        .eq('id', userId)
        .single();

      if (error) {
        console.error('[Auth] Error fetching profile:', error);
        profileFetchRef.current = null;
        return;
      }

      // Fetch user roles from the secure user_roles table
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (rolesError) {
        console.error('[Auth] Error fetching roles:', rolesError);
      }

      // Combine profile with roles
      const profileWithRoles = {
        ...data,
        roles: rolesData?.map(r => r.role) || []
      } as UserProfile;

      setProfile(profileWithRoles);
    } catch (error) {
      console.error('[Auth] Profile fetch exception:', error);
    } finally {
      profileFetchRef.current = null;
    }
  };

  useEffect(() => {
    // Prevent multiple initializations
    if (initializingRef.current) {
      return;
    }
    
    initializingRef.current = true;
    
    let mounted = true;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        
        console.log('[Auth] State change:', event, session?.user?.id);
        
        // Handle refresh token errors by signing out
        if (event === 'TOKEN_REFRESHED' && !session) {
          // Security: Only log errors in development
          if (import.meta.env.DEV) {
            console.log('[Auth] Token refresh failed, signing out');
          }
          supabase.auth.signOut();
          return;
        }
        
        // Update session and user state
        setSession(session);
        setUser(session?.user ?? null);
        
        // Handle profile fetching for authenticated users
        if (session?.user && event !== 'SIGNED_OUT') {
          // Use setTimeout to prevent blocking the auth state change
          setTimeout(() => {
            if (mounted && session?.user) {
              fetchProfile(session.user.id);
            }
          }, 0);
        } else {
          setProfile(null);
          profileFetchRef.current = null;
          
          // Clear invalid sessions from localStorage
          if (event === 'SIGNED_OUT') {
            localStorage.removeItem('sb-cmymuddenlksmwkxzxqr-auth-token');
          }
        }
        
        // Mark as initialized and stop loading after a brief delay
        setTimeout(() => {
          if (mounted) {
            setIsInitialized(true);
            setLoading(false);
          }
        }, 100);
      }
    );

    // Get initial session
    const initializeSession = async () => {
      try {
        // Security: Only log in development
        if (import.meta.env.DEV) {
          console.log('[Auth] Initializing session...');
        }
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[Auth] Session check error:', error);
          // If refresh token is invalid, clear the session
          if (error.message?.includes('refresh_token_not_found') || error.message?.includes('Invalid Refresh Token')) {
            console.log('[Auth] Invalid refresh token detected, clearing session');
            await supabase.auth.signOut();
            return;
          }
        }
        
        if (!mounted) return;
        
        // Update state with initial session
        setSession(session);
        setUser(session?.user ?? null);
        
        // Fetch profile if user exists
        if (session?.user) {
          setTimeout(() => {
            if (mounted) {
              fetchProfile(session.user.id);
            }
          }, 0);
        }
        
        // Mark as initialized with a small delay to prevent flashing
        setTimeout(() => {
          if (mounted) {
            setIsInitialized(true);
            setLoading(false);
          }
        }, 200);
        
      } catch (error) {
        console.error('[Auth] Session initialization error:', error);
        if (mounted) {
          setTimeout(() => {
            setIsInitialized(true);
            setLoading(false);
          }, 200);
        }
      }
    };

    // Initialize session
    initializeSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
      initializingRef.current = false;
    };
  }, []); // Empty dependency array - only run once

  return {
    user,
    profile,
    session,
    loading,
    isInitialized,
    fetchProfile
  };
};
